import React from "react"
import styled, { css } from "styled-components"
import { CheckMarkNoOutline } from "../../components/shared/svg/check-mark.js"
import { Spacer } from "../../design-system/atoms/layout-primitives"
import { T } from "../../design-system/atoms/text"
import VariantContainer, {
  IStyleVariant,
} from "../../design-system/atoms/variant-container"
import ContentBlock from "../../design-system/molecules/content-block"
import IconTitle from "../../design-system/molecules/icon-title"
import ClientsGrid from "../../design-system/organisms/clients-grid"
import {
  FadeIn,
  FadeInBottom,
} from "../../design-system/styles/animations.styles"
import {
  IFluidImageQuery,
  IFluidImageQueryArray,
} from "../../design-system/types/imageQuery.types"
import mergeVariants from "../../design-system/util/merge-variants"
import withAnimation from "../../design-system/util/withAnimation"
import AccentTitleDivider from "../shared/accent-title-divider/accent-title-divider"
import Breakpoints from "../shared/breakpoints"
import Grid from "../shared/grid"

const AnimatedVariantContainer = withAnimation(VariantContainer)
const Span = styled.span`
  display: inline-block;
`
const AnimatedSpan = withAnimation(Span)

interface IClientSuccess {
  description: string
  supportItems: string[]
  desktopLogos: IFluidImageQuery
  tabletLogos: IFluidImageQuery
  mobileLogos: IFluidImageQueryArray
  clientGridVariants?: IStyleVariant | any[]
  showDivider?: Boolean
}

export default function ClientSuccess({
  description,
  supportItems,
  desktopLogos,
  tabletLogos,
  mobileLogos,
  clientGridVariants,
  showDivider = false,
}: IClientSuccess) {
  return (
    <>
      <AccentTitleDivider>Committed to Client Success</AccentTitleDivider>
      <Spacer height={{ sm: "48px", lg: "96px" }} />
      <Grid>
        <VariantContainer
          variants={[
            css`
              grid-column: 1/-1;
              display: contents; // grid passthrough
            `,
            null,
            css`
              display: grid;
              grid-column: 1/-1;
              width: auto;
              justify-self: center;
              grid-template-columns: 1fr 1fr;
              grid-column-gap: 48px;
              justify-content: center;
              @media (min-width: ${Breakpoints.lgPlus}) {
                grid-column: 2/-2;
                grid-column-gap: 96px;
              }
            `,
          ]}
        >
          {/* Supporting Checklist */}
          <VariantContainer
            variants={[
              css`
                grid-column: 1/-1;
              `,
              css`
                grid-column: 2/-2;
              `,
              css`
                grid-column: 2;
                grid-row: 1;
              `,
            ]}
          >
            <VariantContainer
              variants={[
                null,
                css`
                  // need grid in md for dual column display
                  display: grid;
                `,
                css`
                  display: block;
                `,
              ]}
            >
              <ContentBlock.H3
                title={
                  <AnimatedSpan animation={FadeIn}>
                    Supporting Clients
                  </AnimatedSpan>
                }
                description={
                  <AnimatedSpan animation={FadeIn}>{description}</AnimatedSpan>
                }
                titleVariants={[
                  css`
                    text-align: center;
                  `,
                  null,
                  css`
                    text-align: left;
                  `,
                ]}
                bodyVariants={[
                  css`
                    text-align: center;
                    font-weight: 300;
                  `,
                  css`
                    font-weight: 300;
                  `,
                  css`
                    text-align: left;
                    font-weight: 300;
                  `,
                ]}
              />
              <VariantContainer
                variants={{
                  md: css`
                    // center the 2 column grid
                    justify-self: center;
                  `,
                }}
              >
                <AnimatedVariantContainer
                  animation={FadeInBottom}
                  variants={[
                    css`
                      ${T.body.small`400`};
                      display: grid;
                      justify-content: center;
                      grid-gap: 16px;
                      color: var(--slate);
                      padding: 32px 0 48px 0;
                    `,
                    css`
                      ${T.md.body`400`};
                      grid-template-columns: 1fr 1fr;
                      grid-template-rows: repeat(
                        ${Math.ceil(supportItems.length / 2)},
                        1fr
                      );
                      // Items fill all rows in column before moving to next
                      grid-auto-flow: column;
                      grid-gap: 24px 48px;
                    `,
                    css`
                      ${T.lg.body`400`};
                      justify-self: left;
                      // Single rowed column
                      grid-template-columns: 1fr;
                      grid-template-rows: repeat(${supportItems.length}, 1fr);
                      grid-gap: 24px;
                      padding: 48px 0 0 0;
                    `,
                  ]}
                >
                  {supportItems.map((item, idx) => (
                    <IconTitle
                      // color=var(--emerald)
                      icon={<CheckMarkNoOutline color="#3e8551" />}
                      title={item}
                      key={idx}
                    />
                  ))}
                </AnimatedVariantContainer>
              </VariantContainer>
            </VariantContainer>
          </VariantContainer>
          {/* Logos Grid */}
          <AnimatedVariantContainer
            animation={FadeIn}
            variants={mergeVariants(
              [
                css`
                  grid-column: 1/-1;
                  width: 100%;
                  justify-self: center;
                `,
                css`
                  grid-column: 2/-2;
                `,
                css`
                  grid-column: 1;
                  grid-row: 1;
                  justify-self: right;
                `,
              ],
              clientGridVariants
            )}
          >
            <ClientsGrid
              titleVariants={[
                css`
                  display: none;
                `,
              ]}
              blockVariants={[
                css`
                  padding: 32px;
                  .slick-dots {
                    bottom: -75px;
                  }
                `,
                css`
                  padding: 32px;
                `,
                css`
                  padding: 32px;
                `,
              ]}
              mobileLogos={mobileLogos.edges}
              tabletLogos={tabletLogos.childImageSharp.fluid}
              desktopLogos={desktopLogos.childImageSharp.fluid}
            />
          </AnimatedVariantContainer>
        </VariantContainer>
      </Grid>
      <Spacer height={{ sm: "96px", md: "64px", lg: "96px" }} />
      {showDivider && (
        <AnimatedVariantContainer
          animation={FadeIn}
          variants={[
            css`
              border-top: 1px solid var(--dark-mode-heading);
              width: 100%;
            `,
          ]}
        />
      )}
    </>
  )
}
